"use client";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { Separator } from "@/src/components/shadcn/ui/separator";
import type { ReferralMatchMetadata } from "@/src/models/task-approval";

export function ReferralMatchEvidence({ metadata }: { metadata: ReferralMatchMetadata | null }) {
  if (!metadata) {
    return <p className="text-sm text-muted-foreground">No match details available.</p>;
  }

  const { evidence, evaluation } = metadata;
  // `evidence.openReferrals` may be a filtered subset (e.g. PA East passes
  // date-window candidates only), so look up by `index` rather than position.
  const matchedReferral = evidence.openReferrals.find((r) => r.index === evaluation.matchedIndex);

  return (
    <div className="flex flex-col gap-4 text-sm">
      {metadata.instruction && <p className="text-muted-foreground">{metadata.instruction}</p>}

      <Separator />

      {matchedReferral && (
        <div className="flex flex-col gap-1.5">
          <p className="font-semibold">Matched Referral</p>
          <div className="pl-3 flex flex-col gap-1 text-muted-foreground">
            {matchedReferral.specialty && (
              <p>
                <span className="text-foreground font-medium">Specialty:</span> {matchedReferral.specialty}
              </p>
            )}
            {matchedReferral.performerName && (
              <p>
                <span className="text-foreground font-medium">Referred to:</span> {matchedReferral.performerName}
              </p>
            )}
            {matchedReferral.performerAddress && (
              <p>
                <span className="text-foreground font-medium">Address:</span> {matchedReferral.performerAddress}
              </p>
            )}
            {matchedReferral.reasonText && (
              <p>
                <span className="text-foreground font-medium">Reason:</span> {matchedReferral.reasonText}
              </p>
            )}
            {matchedReferral.icdCodes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-foreground font-medium">ICD-10:</span>
                {matchedReferral.icdCodes.map((code) => (
                  <Badge key={code} variant="outline" className="text-xs">
                    {code}
                  </Badge>
                ))}
              </div>
            )}
            {matchedReferral.startDate && (
              <p>
                <span className="text-foreground font-medium">Referral start:</span>{" "}
                {new Date(matchedReferral.startDate).toLocaleDateString()}
              </p>
            )}
            {matchedReferral.endDate && (
              <p>
                <span className="text-foreground font-medium">Referral end:</span>{" "}
                {new Date(matchedReferral.endDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col gap-1.5">
        <p className="font-semibold">Document</p>
        <div className="pl-3 flex flex-col gap-1 text-muted-foreground">
          {evidence.progressNote.clinicName && (
            <p>
              <span className="text-foreground font-medium">Clinic:</span> {evidence.progressNote.clinicName}
            </p>
          )}
          {evidence.progressNote.clinicAddress && (
            <p>
              <span className="text-foreground font-medium">Clinic address:</span> {evidence.progressNote.clinicAddress}
            </p>
          )}
          {evidence.progressNote.providerName && (
            <p>
              <span className="text-foreground font-medium">Provider:</span> {evidence.progressNote.providerName}
            </p>
          )}
          {evidence.progressNote.specialtyFolder && (
            <p>
              <span className="text-foreground font-medium">Specialty:</span> {evidence.progressNote.specialtyFolder}
            </p>
          )}
          {evidence.progressNote.reasonForVisit && (
            <p>
              <span className="text-foreground font-medium">Reason:</span> {evidence.progressNote.reasonForVisit}
            </p>
          )}
          {evidence.progressNote.dateOfEvent && (
            <p>
              <span className="text-foreground font-medium">Date of service:</span> {evidence.progressNote.dateOfEvent}
            </p>
          )}
          {evidence.progressNote.diagnosisCodes.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-foreground font-medium">Diagnoses:</span>
              {evidence.progressNote.diagnosisCodes.map((code) => (
                <Badge key={code} variant="outline" className="text-xs">
                  {code}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-1.5">
        <p className="font-semibold">Match Reasoning</p>
        <ul className="pl-3 flex flex-col gap-1 list-disc list-inside text-muted-foreground">
          {evaluation.reasoning.map((reason, i) => (
            <li key={i}>{reason}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
